// src/components/Statistics.js
// Statistiques — /api/statistics/overview rendered as one visual system:
// Chart.js for the time series, the motifs bar chart and the motif × carrier
// stacked matrix; every other ranking uses the app's proportion-row language
// (label · count · one-hue bar). Semantic colors appear only where they mean
// something: the sentiment composition bar and the status dots. Motif series
// hues are assigned alphabetically so they are stable across filters.
// Filters (brand, client, carrier, period) live in the URL.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { FaSync, FaTimesCircle } from 'react-icons/fa';
import { getStatusLabel, getStatusClass } from '../constants/ticketStatuses';
import './Statistics.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

const PERIODS = ['7', '30', '90', '365', 'custom'];

// Motif series palette — hues are assigned to motif names sorted alphabetically
// (locale fr) so a motif keeps its color when a filter changes the visible set.
// The backend caps the matrix at 6 motifs + "Autres"; never cycle or extend.
const MOTIF_PALETTE = ['#5E72EB', '#D95926', '#0FA3A3', '#C98500', '#D55181', '#008300', '#9085E9'];
const MOTIF_OTHER_COLOR = '#6D788D'; // "Autres" — deliberately recessive gray
const CARD_SURFACE = '#151A24'; // --bg-surface; borderColor on stacked segments = 2px gaps

// Shared dark tooltip style (same as the activity Line chart).
const DARK_TOOLTIP = {
  backgroundColor: 'rgba(21, 26, 36, 0.96)',
  titleColor: '#E7EBF3',
  bodyColor: '#9AA5BA',
  padding: 10,
  cornerRadius: 8,
  borderColor: 'rgba(148, 163, 184, 0.28)',
  borderWidth: 1,
};

// Status semantic family → dot color (bars stay neutral; the dot carries the
// meaning, mirroring the app-wide status chip language).
const STATUS_DOT = {
  'status-resolved': 'var(--state-success)',
  'status-closed': 'var(--text-muted)',
  'status-waiting': 'var(--state-warning)',
  'status-pending': 'var(--state-warning)',
  'status-automated': 'var(--violet)',
  'status-processing': 'var(--blue)',
  'status-open': 'var(--blue)',
  'status-new': 'var(--blue)',
};

const TOOL_LABELS = {
  retrieve_knowledge: 'tools.retrieveKnowledge',
  assign_to_employee: 'tools.assignToEmployee',
  resolve_ticket: 'tools.resolveTicket',
  Close_ticket: 'tools.closeTicket',
  get_tracking_info: 'tools.getTrackingInfo',
  Retrieve_customer_order_by_reference_and_email: 'tools.retrieveOrder',
  Update_customer_shipping_address: 'tools.updateShippingAddress',
  find_relay_points: 'tools.findRelayPoints',
  create_return_with_edn_label: 'tools.createReturn',
  carriers_conversation: 'tools.carriersConversation',
  Retour: 'tools.return',
  fill_form_fields: 'tools.fillPDF',
  getOrderById: 'tools.getOrderById',
  carriers_conversation_update: 'tools.carriersConversationUpdate',
  order_status: 'tools.orderStatus',
  Request_return_order: 'tools.Request_return_order',
  Retrieve_customer_order_by_ID: 'tools.Retrieve_customer_order_by_ID',
  Request_exchange_shopify: 'tools.Request_exchange_shopify',
  Get_all_product_variants: 'tools.Get_all_product_variants',
};

const Statistics = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedBrand = searchParams.get('brand') || '';
  const selectedClient = searchParams.get('client') || '';
  const selectedCarrier = searchParams.get('carrier') || '';
  const daysParam = searchParams.get('days') || '30';
  const dateRange = PERIODS.includes(daysParam) ? daysParam : '30';
  const customStartDate = searchParams.get('start') || '';
  const customEndDate = searchParams.get('end') || '';

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace: true });
  };

  const [overviewData, setOverviewData] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState([]); // empty = select hidden (incl. 403/fetch failure)
  const [carrierOptions, setCarrierOptions] = useState([]);
  const requestSeq = useRef(0);

  useEffect(() => {
    axios.get('/api/brands/list', {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    })
      .then((res) => setBrands(res.data || []))
      .catch((err) => console.error('Error fetching brands:', err));
  }, []);

  useEffect(() => {
    axios.get('/api/logistics/clients-edn', {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setClients([...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr')));
      })
      .catch((err) => console.error('Error fetching clients:', err)); // keep hidden on failure
  }, []);

  useEffect(() => {
    axios.get('/api/statistics/carriers?limit=100', {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    })
      .then((res) => {
        const list = res.data?.carriers || [];
        setCarrierOptions([...list].sort((a, b) => (b.count || 0) - (a.count || 0)));
      })
      .catch((err) => console.error('Error fetching carriers:', err));
  }, []);

  const fetchStatistics = useCallback(async () => {
    const seq = ++requestSeq.current;
    setOverviewData(null);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedBrand) params.append('brand_id', selectedBrand);
      if (selectedClient) params.append('client_id', selectedClient);
      if (selectedCarrier) params.append('carrier', selectedCarrier);
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      } else {
        params.append('days', dateRange === 'custom' ? '30' : dateRange);
      }
      const response = await axios.get(`/api/statistics/overview?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (seq === requestSeq.current) setOverviewData(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      if (seq === requestSeq.current) {
        setError(t('statisticsPage.fetchError', 'Impossible de charger les statistiques.'));
        setOverviewData({});
      }
    }
  }, [selectedBrand, selectedClient, selectedCarrier, dateRange, customStartDate, customEndDate, t]);

  useEffect(() => { fetchStatistics(); }, [fetchStatistics]);

  // ─────────────────────────────────────────────────────────── formatting

  const fmtNum = (n) => (typeof n === 'number' ? n.toLocaleString(lang) : '—');

  const fmtDuration = (minutes) => {
    if (typeof minutes !== 'number' || minutes <= 0) return null;
    const m = Math.round(minutes);
    if (m < 60) return t('statisticsPage.durationMin', '{{m}} min', { m });
    if (m < 1440) {
      return t('statisticsPage.durationHours', '{{h}} h {{m}} min', { h: Math.floor(m / 60), m: m % 60 });
    }
    return t('statisticsPage.durationDays', '{{d}} j {{h}} h', { d: Math.floor(m / 1440), h: Math.floor((m % 1440) / 60) });
  };

  // Compact resolution-time format: hours below 48h, days (1 decimal) above.
  const fmtHours = (hours) => {
    if (typeof hours !== 'number' || hours < 0) return null;
    if (hours < 48) return t('statisticsPage.durationHoursCompact', '{{h}}h', { h: Math.round(hours) });
    return t('statisticsPage.durationDaysCompact', '{{d}}j', { d: (hours / 24).toFixed(1) });
  };

  const fmtDay = (dateStr) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(lang, { day: 'numeric', month: 'short' });
  };

  const periodLabel = dateRange === 'custom' && customStartDate && customEndDate
    ? t('statisticsPage.customPeriodLabel', 'du {{start}} au {{end}}', { start: fmtDay(customStartDate), end: fmtDay(customEndDate) })
    : {
        '7': t('statisticsPage.last7Days', '7 derniers jours'),
        '30': t('statisticsPage.last30Days', '30 derniers jours'),
        '90': t('statisticsPage.last90Days', '90 derniers jours'),
        '365': t('statisticsPage.lastYear', '12 derniers mois'),
      }[dateRange === 'custom' ? '30' : dateRange];

  const sentimentTone = (score) => (score <= 39 ? 'low' : score <= 65 ? 'mid' : 'high');

  const toolLabel = (name) => (TOOL_LABELS[name] ? t(TOOL_LABELS[name], name) : name);

  // Top-N rollup: keep n rows, sum the tail into "Autres (k)".
  const rollup = (items, n, getLabel, getCount) => {
    if (!items || items.length <= n) return (items || []).map((it) => ({ item: it, label: getLabel(it), count: getCount(it) }));
    const head = items.slice(0, n).map((it) => ({ item: it, label: getLabel(it), count: getCount(it) }));
    const tail = items.slice(n);
    head.push({
      item: null,
      label: t('statisticsPage.othersRollup', 'Autres ({{total}})', { total: tail.length }),
      count: tail.reduce((sum, it) => sum + getCount(it), 0),
      muted: true,
    });
    return head;
  };

  // A carrier's split by service (« Locker 30 · Point Relais 12 · non précisé 40 »):
  // the carrier rows merge every name its services are written with.
  const serviceSummary = (services, max = 3) => {
    const list = services || [];
    if (!list.length || (list.length === 1 && !list[0].service)) return '';
    const parts = list.map((s) => `${s.service_label || t('statisticsPage.serviceUnspecified', 'service non précisé')} ${fmtNum(s.count)}`);
    return parts.length > max ? `${parts.slice(0, max).join(' · ')} · …` : parts.join(' · ');
  };

  // ─────────────────────────────────────────────────────────── components

  const BreakdownCard = ({ title, meta, rows, tone = 'accent', totalForPct, children }) => {
    const max = Math.max(1, ...rows.map((r) => r.count));
    return (
      <div className="st-card">
        <div className="st-card-header">
          <h2>{title}</h2>
          {meta && <span className="st-card-meta">{meta}</span>}
        </div>
        {rows.length === 0 ? (
          <p className="st-card-empty">{t('statisticsPage.noData', 'Aucune donnée sur la période.')}</p>
        ) : (
          <div className="st-breakdown">
            {rows.map((row) => {
              const pct = totalForPct > 0 ? (row.count / totalForPct) * 100 : 0;
              return (
                <div key={row.key || row.label} className="st-breakdown-row">
                  <span className={`st-breakdown-label ${row.muted ? 'muted' : ''}`} title={row.title || row.label}>
                    {row.dot && <span className="st-status-dot" style={{ background: row.dot }} aria-hidden="true" />}
                    <span className="st-breakdown-label-text">
                      {row.label}
                      {row.subLabel && <small>{row.subLabel}</small>}
                    </span>
                  </span>
                  <span className="st-breakdown-count">{fmtNum(row.count)}</span>
                  <span className="st-breakdown-bar">
                    <span
                      className={`st-breakdown-fill ${tone} ${row.muted ? 'muted' : ''}`}
                      style={{ width: `${(row.count / max) * 100}%` }}
                    />
                  </span>
                  <span className="st-breakdown-pct">{pct >= 10 ? Math.round(pct) : pct.toFixed(1)} %</span>
                </div>
              );
            })}
          </div>
        )}
        {children}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────── chart

  const daily = overviewData?.tickets_per_day?.daily_data || [];

  // Outlier guard: one bulk-import day (e.g. 8k tickets created on 08/09)
  // would flatten every other day against the axis and the chart would carry
  // no information. Cap the y-axis near the 95th percentile and name the
  // clipped day(s) under the chart; tooltips keep the true values.
  const seriesValues = daily
    .flatMap((d) => [d.total || 0, d.resolved || 0])
    .sort((a, b) => a - b);
  const seriesP95 = seriesValues.length
    ? seriesValues[Math.min(seriesValues.length - 1, Math.floor(seriesValues.length * 0.95))]
    : 0;
  const seriesMax = seriesValues.length ? seriesValues[seriesValues.length - 1] : 0;
  const yCap = seriesP95 > 0 && seriesMax > seriesP95 * 3 ? Math.ceil(seriesP95 * 1.5) : null;
  const clippedDays = yCap
    ? daily.filter((d) => (d.total || 0) > yCap || (d.resolved || 0) > yCap)
    : [];

  const trendData = daily.length > 0 ? {
    labels: daily.map((d) => d.date),
    datasets: [
      {
        label: t('statisticsPage.totalTickets', 'Tickets'),
        data: daily.map((d) => d.total),
        borderColor: '#3FBDB4',
        backgroundColor: 'rgba(63, 189, 180, 0.08)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: '#3FBDB4',
        fill: true,
        tension: 0.3,
      },
      {
        label: t('statisticsPage.resolved', 'Résolus'),
        data: daily.map((d) => d.resolved),
        borderColor: '#4ADE80',
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: '#4ADE80',
        fill: false,
        tension: 0.3,
      },
    ],
  } : null;

  const trendOptions = {
    maintainAspectRatio: false,
    responsive: true,
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: '#9AA5BA',
          padding: 16,
          font: { size: 11, family: "'Inter', sans-serif" },
          usePointStyle: true,
          pointStyle: 'line',
          boxWidth: 18,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(21, 26, 36, 0.96)',
        titleColor: '#E7EBF3',
        bodyColor: '#9AA5BA',
        padding: 10,
        cornerRadius: 8,
        borderColor: 'rgba(148, 163, 184, 0.28)',
        borderWidth: 1,
        callbacks: {
          title: (items) => (items.length > 0 ? fmtDay(items[0].label) : ''),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#6D788D',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8,
          font: { size: 10.5 },
          callback(value) { return fmtDay(this.getLabelForValue(value)); },
        },
        border: { display: false },
      },
      y: {
        grid: { color: 'rgba(148, 163, 184, 0.08)' },
        ticks: { color: '#6D788D', padding: 8, font: { size: 10.5 }, precision: 0 },
        border: { display: false },
        beginAtZero: true,
        ...(yCap ? { max: yCap } : {}),
      },
    },
  };

  // ─────────────────────────────────────────────────────────── derived

  const loading = overviewData === null;
  const totalTickets = overviewData?.tickets_per_day?.total_tickets;
  const resolvedTotal = daily.length > 0 ? daily.reduce((sum, d) => sum + (d.resolved || 0), 0) : null;
  const avgPerDay = overviewData?.tickets_per_day?.average_per_day;
  const sentiment = overviewData?.sentiment;
  const responseTime = overviewData?.response_time;
  const aiTools = overviewData?.ai_tools;
  const toolsPerTicket = overviewData?.tools_per_ticket;
  const aiRuns = overviewData?.ai_runs; // AI trace KPIs (docs/ai-trace-spec.md §6.2)

  // Sentiment buckets in reading order, semantic colors — the one place they
  // belong.
  const SENTIMENT_META = {
    Positive: { label: t('statisticsPage.sentimentPositive', 'Positif'), color: 'var(--state-success)', cls: 'positive' },
    Neutral: { label: t('statisticsPage.sentimentNeutral', 'Neutre'), color: 'var(--state-warning)', cls: 'neutral' },
    Negative: { label: t('statisticsPage.sentimentNegative', 'Négatif'), color: 'var(--state-danger)', cls: 'negative' },
  };
  const sentimentBuckets = ['Positive', 'Neutral', 'Negative']
    .map((key) => {
      const found = (sentiment?.distribution || []).find((s) => s.bucket === key);
      return found ? { key, ...SENTIMENT_META[key], count: found.count, percentage: found.percentage } : null;
    })
    .filter(Boolean);
  const sentimentTotal = sentiment?.total_tickets || 0;

  // Brands: merge duplicate labels (the API can emit several "No Brand" rows)
  const brandRowsRaw = (() => {
    const merged = new Map();
    (overviewData?.brands?.brands || []).forEach((b) => {
      const label = (!b.brand_name || b.brand_name === 'No Brand')
        ? t('statisticsPage.noBrand', 'Sans marque')
        : b.brand_name;
      merged.set(label, (merged.get(label) || 0) + (b.ticket_count || 0));
    });
    return [...merged.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  })();

  // Tools-per-ticket mini histogram: buckets 0..6, then "7+"
  const toolsHistogram = (() => {
    const dist = toolsPerTicket?.distribution || [];
    if (dist.length === 0) return [];
    const buckets = [];
    let overflow = 0;
    dist.forEach((d) => {
      if (d.tools_count <= 6) buckets.push({ label: String(d.tools_count), count: d.ticket_count });
      else overflow += d.ticket_count;
    });
    if (overflow > 0) buckets.push({ label: '7+', count: overflow });
    return buckets;
  })();
  const maxHistogram = Math.max(1, ...toolsHistogram.map((b) => b.count));

  // ─────────────────────────────────────────────────────── motifs & carriers

  const resolution = overviewData?.resolution;
  const motifsData = overviewData?.motifs;
  const motifRows = (motifsData?.motifs || []).slice(0, 12); // backend order = volume desc

  const motifChartData = motifRows.length > 0 ? {
    labels: motifRows.map((m) => m.motif),
    datasets: [
      {
        data: motifRows.map((m) => m.count),
        backgroundColor: '#5E72EB',
        borderRadius: 4,
        maxBarThickness: 18,
      },
    ],
  } : null;

  const motifChartOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false }, // single series — the card title names it
      tooltip: {
        ...DARK_TOOLTIP,
        callbacks: {
          label: (item) => {
            const row = motifRows[item.dataIndex];
            const pct = typeof row?.percentage === 'number'
              ? (row.percentage >= 10 ? Math.round(row.percentage) : row.percentage.toFixed(1))
              : null;
            return pct === null
              ? fmtNum(item.parsed.x)
              : t('statisticsPage.motifTooltip', '{{count}} tickets · {{pct}} % des classés', { count: fmtNum(item.parsed.x), pct });
          },
        },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(148, 163, 184, 0.08)' },
        ticks: { color: '#6D788D', font: { size: 10.5 }, precision: 0 },
        border: { display: false },
        beginAtZero: true,
      },
      y: {
        grid: { display: false },
        ticks: { color: '#9AA5BA', font: { size: 11 } },
        border: { display: false },
      },
    },
  };

  // Motif × carrier matrix — stacked horizontal bars, one dataset per motif.
  const matrix = overviewData?.motif_carrier_matrix;
  const matrixRows = matrix?.rows || [];
  const matrixMotifNames = matrix?.motifs || [];
  // "Autres" (last name when has_other) keeps a fixed gray and stays out of the
  // alphabetical color assignment, so real motifs keep their hue across filters.
  const matrixOtherName = matrix?.has_other && matrixMotifNames.length > 0
    ? matrixMotifNames[matrixMotifNames.length - 1]
    : null;
  const motifColorSlots = matrixMotifNames
    .filter((name) => name !== matrixOtherName)
    .sort((a, b) => a.localeCompare(b, 'fr'));
  const motifColor = (name) => (
    name === matrixOtherName ? MOTIF_OTHER_COLOR : MOTIF_PALETTE[motifColorSlots.indexOf(name)]
  );

  const matrixChartData = matrixRows.length > 0 ? {
    labels: matrixRows.map((r) => r.carrier),
    datasets: matrixMotifNames.map((name) => ({
      label: name,
      data: matrixRows.map((r) => (name === matrixOtherName ? (r.other || 0) : (r.cells?.[name] || 0))),
      backgroundColor: motifColor(name),
      borderColor: CARD_SURFACE, // 2px surface-colored gaps between segments
      borderWidth: 2,
      borderSkipped: false,
      maxBarThickness: 22,
    })),
  } : null;

  const matrixChartOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    interaction: { intersect: false, mode: 'y' },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#9AA5BA',
          padding: 14,
          font: { size: 11, family: "'Inter', sans-serif" },
          usePointStyle: true,
          pointStyle: 'rectRounded',
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        ...DARK_TOOLTIP,
        mode: 'y',
        intersect: false,
        filter: (item) => item.parsed.x > 0,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { color: 'rgba(148, 163, 184, 0.08)' },
        ticks: { color: '#6D788D', font: { size: 10.5 }, precision: 0 },
        border: { display: false },
        beginAtZero: true,
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: { color: '#9AA5BA', font: { size: 11 } },
        border: { display: false },
      },
    },
  };

  const carrierPerf = overviewData?.carrier_performance?.carriers || [];
  const maxCarrierTotal = Math.max(1, ...carrierPerf.map((c) => c.total || 0));

  const topClients = overviewData?.top_clients;
  const topClientRows = topClients?.clients || [];

  const fmtPct = (value) => (
    typeof value === 'number'
      ? `${value >= 10 ? Math.round(value) : Number(value.toFixed(1))} %`
      : '—'
  );

  // ─────────────────────────────────────────────────────────── render

  return (
    <div className="statistics-page">
      {/* Header + toolbar */}
      <div className="st-header">
        <div className="st-header-left">
          <h1>{t('statisticsPage.title', 'Statistiques')}</h1>
          <p className="st-header-subtitle">
            {t('statisticsPage.subtitle', 'Activité du support et de l’IA · {{period}}', { period: periodLabel })}
          </p>
        </div>
        <div className="st-toolbar">
          {brands.length > 0 && (
            <label className="st-toolbar-field">
              <span>{t('statisticsPage.brand', 'Marque')}</span>
              <select value={selectedBrand} onChange={(e) => updateParams({ brand: e.target.value })}>
                <option value="">{t('statisticsPage.allBrands', 'Toutes les marques')}</option>
                {brands.map((brand) => (
                  <option key={brand.id || brand._id} value={brand.id || brand._id}>{brand.name}</option>
                ))}
              </select>
            </label>
          )}
          {clients.length > 0 && (
            <label className="st-toolbar-field">
              <span>{t('statisticsPage.client', 'Client')}</span>
              <select value={selectedClient} onChange={(e) => updateParams({ client: e.target.value })}>
                <option value="">{t('statisticsPage.allClients', 'Tous les clients')}</option>
                {clients.map((client) => (
                  // clients-edn serializes the Mongo _id as `id`
                  <option key={client.id || client._id} value={client.id || client._id}>{client.name}</option>
                ))}
              </select>
            </label>
          )}
          {(carrierOptions.length > 0 || selectedCarrier) && (
            <label className="st-toolbar-field">
              <span>{t('statisticsPage.carrier', 'Transporteur')}</span>
              <select value={selectedCarrier} onChange={(e) => updateParams({ carrier: e.target.value })}>
                <option value="">{t('statisticsPage.allCarriers', 'Tous les transporteurs')}</option>
                {carrierOptions.map((c) => (
                  <option key={c.carrier} value={c.carrier}>{c.carrier}</option>
                ))}
                {/* keep an unknown URL value selectable so it never silently disappears */}
                {selectedCarrier && !carrierOptions.some((c) => c.carrier === selectedCarrier) && (
                  <option value={selectedCarrier}>{selectedCarrier}</option>
                )}
              </select>
            </label>
          )}
          <label className="st-toolbar-field">
            <span>{t('statisticsPage.period', 'Période')}</span>
            <select value={dateRange} onChange={(e) => updateParams({ days: e.target.value === '30' ? null : e.target.value })}>
              <option value="7">{t('statisticsPage.last7Days', '7 derniers jours')}</option>
              <option value="30">{t('statisticsPage.last30Days', '30 derniers jours')}</option>
              <option value="90">{t('statisticsPage.last90Days', '90 derniers jours')}</option>
              <option value="365">{t('statisticsPage.lastYear', '12 derniers mois')}</option>
              <option value="custom">{t('statisticsPage.custom', 'Personnalisée')}</option>
            </select>
          </label>
          {dateRange === 'custom' && (
            <>
              <label className="st-toolbar-field">
                <span>{t('statisticsPage.startDate', 'Début')}</span>
                <input type="date" value={customStartDate} onChange={(e) => updateParams({ start: e.target.value })} />
              </label>
              <label className="st-toolbar-field">
                <span>{t('statisticsPage.endDate', 'Fin')}</span>
                <input type="date" value={customEndDate} onChange={(e) => updateParams({ end: e.target.value })} />
              </label>
            </>
          )}
          <button
            type="button"
            className="st-refresh-btn"
            onClick={fetchStatistics}
            title={t('statisticsPage.refresh', 'Actualiser')}
            aria-label={t('statisticsPage.refresh', 'Actualiser')}
          >
            <FaSync />
          </button>
        </div>
      </div>

      {error && (
        <div className="st-error" role="alert">
          <FaTimesCircle /> {error}
          <button type="button" className="u-btn u-btn--secondary" onClick={fetchStatistics}>
            {t('statisticsPage.retry', 'Réessayer')}
          </button>
        </div>
      )}

      {loading ? (
        <div className="st-skeleton" aria-hidden="true">
          <div className="st-skeleton-row st-skeleton-kpi" />
          <div className="st-skeleton-row st-skeleton-chart" />
          <div className="st-skeleton-grid">
            {Array.from({ length: 4 }, (_, i) => <div key={i} className="st-skeleton-row st-skeleton-card" />)}
          </div>
        </div>
      ) : (
        <>
          {/* KPI strip — conditional on field presence, scoped to the period */}
          <div className="st-kpi-strip">
            {typeof totalTickets === 'number' && (
              <div className="st-kpi">
                <span className="st-kpi-value">{fmtNum(totalTickets)}</span>
                <span className="st-kpi-label">{t('statisticsPage.kpiTickets', 'tickets')}</span>
                {typeof avgPerDay === 'number' && avgPerDay > 0 && (
                  <span className="st-kpi-sub">{t('statisticsPage.kpiPerDay', '≈ {{avg}}/jour', { avg: avgPerDay.toFixed(1) })}</span>
                )}
              </div>
            )}
            {typeof resolvedTotal === 'number' && typeof totalTickets === 'number' && totalTickets > 0 && (
              <div className="st-kpi">
                <span className="st-kpi-value">{fmtNum(resolvedTotal)}</span>
                <span className="st-kpi-label">{t('statisticsPage.kpiResolved', 'résolus')}</span>
                <span className="st-kpi-sub">
                  {t('statisticsPage.kpiResolvedRate', '{{rate}} % des tickets', { rate: Math.round((resolvedTotal / totalTickets) * 100) })}
                </span>
              </div>
            )}
            {sentiment && sentimentTotal > 0 && typeof sentiment.overall_average === 'number' && (
              <div className="st-kpi">
                <span className="st-kpi-value">
                  <span className={`st-tone-dot ${sentimentTone(sentiment.overall_average)}`} aria-hidden="true" />
                  {Math.round(sentiment.overall_average)}
                  <span className="st-kpi-unit">/100</span>
                </span>
                <span className="st-kpi-label">{t('statisticsPage.kpiSentiment', 'sentiment moyen')}</span>
                <span className="st-kpi-sub">{t('statisticsPage.kpiSentimentScope', 'sur {{total}} tickets analysés', { total: fmtNum(sentimentTotal) })}</span>
              </div>
            )}
            {responseTime && responseTime.tickets_analyzed > 0 && fmtDuration(responseTime.avg_response_time_minutes) && (
              <div className="st-kpi">
                <span className="st-kpi-value">{fmtDuration(responseTime.avg_response_time_minutes)}</span>
                <span className="st-kpi-label">{t('statisticsPage.kpiResponse', '1ʳᵉ réponse (moy.)')}</span>
                <span className="st-kpi-sub">{t('statisticsPage.kpiResponseScope', 'sur {{total}} tickets', { total: fmtNum(responseTime.tickets_analyzed) })}</span>
              </div>
            )}
            {typeof resolution?.median_resolution_hours === 'number' && (
              <div className="st-kpi">
                <span className="st-kpi-value">{fmtHours(resolution.median_resolution_hours)}</span>
                <span className="st-kpi-label">{t('statisticsPage.kpiMedianResolution', 'résolution (médiane)')}</span>
                {typeof resolution.avg_resolution_hours === 'number' && (
                  <span className="st-kpi-sub">
                    {t('statisticsPage.kpiAvgResolution', 'moyenne {{avg}}', { avg: fmtHours(resolution.avg_resolution_hours) })}
                  </span>
                )}
              </div>
            )}
            {typeof resolution?.ai_share_of_resolutions === 'number' && (
              <div className="st-kpi">
                <span className="st-kpi-value">
                  {Math.round(resolution.ai_share_of_resolutions)}
                  <span className="st-kpi-unit">%</span>
                </span>
                <span className="st-kpi-label">{t('statisticsPage.kpiAiShare', 'des résolutions gérées par l’IA')}</span>
                <span className="st-kpi-sub">
                  {t('statisticsPage.kpiAiShareScope', '{{total}} tickets résolus par l’IA', { total: fmtNum(resolution.ai_resolved_tickets) })}
                </span>
              </div>
            )}
            {aiTools && typeof aiTools.total_tool_calls === 'number' && aiTools.total_tool_calls > 0 && (
              <div className="st-kpi">
                <span className="st-kpi-value">{fmtNum(aiTools.total_tool_calls)}</span>
                <span className="st-kpi-label">{t('statisticsPage.kpiToolCalls', 'appels d’outils IA')}</span>
                {typeof toolsPerTicket?.average_tools_per_ticket === 'number' && toolsPerTicket.average_tools_per_ticket > 0 && (
                  <span className="st-kpi-sub">
                    {t('statisticsPage.kpiToolsPerTicket', '≈ {{avg}} outils/ticket', { avg: toolsPerTicket.average_tools_per_ticket })}
                  </span>
                )}
              </div>
            )}
            {aiRuns && typeof aiRuns.llm_calls_per_ticket === 'number' && aiRuns.tickets_with_runs > 0 && (
              <div className="st-kpi">
                <span className="st-kpi-value">{fmtNum(aiRuns.llm_calls_per_ticket)}</span>
                <span className="st-kpi-label">{t('statisticsPage.kpiLlmCallsPerTicket', 'appels modèle / ticket')}</span>
                <span className="st-kpi-sub">
                  {typeof aiRuns.cost_usd_per_ticket === 'number'
                    ? t('statisticsPage.kpiLlmCostPerTicket', '≈ {{cost}} $ / ticket · {{coverage}} % des tickets tracés', {
                      cost: aiRuns.cost_usd_per_ticket.toFixed(4),
                      coverage: aiRuns.coverage_percentage,
                    })
                    : t('statisticsPage.kpiLlmRunsScope', '{{runs}} runs sur {{tickets}} tickets tracés ({{coverage}} %)', {
                      runs: fmtNum(aiRuns.runs),
                      tickets: fmtNum(aiRuns.tickets_with_runs),
                      coverage: aiRuns.coverage_percentage,
                    })}
                </span>
              </div>
            )}
          </div>

          {/* Activity — the one real chart */}
          {trendData && (
            <div className="st-card st-activity-card">
              <div className="st-card-header">
                <h2>{t('statisticsPage.activityTitle', 'Activité')}</h2>
                {typeof avgPerDay === 'number' && (
                  <span className="st-card-meta">
                    {t('statisticsPage.activityMeta', 'moyenne {{avg}} tickets/jour', { avg: avgPerDay.toFixed(1) })}
                  </span>
                )}
              </div>
              <div className="st-chart-body">
                <Line data={trendData} options={trendOptions} />
              </div>
              {clippedDays.length > 0 && (
                <p className="st-chart-clip-note">
                  {clippedDays
                    .map((d) => t('statisticsPage.activityClipped', 'pic du {{date}} hors échelle ({{value}} tickets)', {
                      date: fmtDay(d.date),
                      value: Math.max(d.total || 0, d.resolved || 0).toLocaleString('fr-FR'),
                    }))
                    .join(' · ')}
                </p>
              )}
            </div>
          )}

          <div className="st-grid">
            {/* Motif × carrier matrix — the centerpiece, full width */}
            {matrix && (
              <div className="st-card st-span-full">
                <div className="st-card-header">
                  <h2>{t('statisticsPage.matrixTitle', 'Motifs par transporteur')}</h2>
                  {typeof matrix.total_tickets === 'number' && matrixRows.length > 0 && (
                    <span className="st-card-meta">
                      {t('statisticsPage.scopeTickets', '{{total}} tickets', { total: fmtNum(matrix.total_tickets) })}
                    </span>
                  )}
                </div>
                {matrixChartData ? (
                  <div className="st-matrix-body" style={{ height: Math.max(220, matrixRows.length * 36 + 76) }}>
                    <Bar data={matrixChartData} options={matrixChartOptions} />
                  </div>
                ) : (
                  <p className="st-card-empty">{t('statisticsPage.noData', 'Aucune donnée sur la période.')}</p>
                )}
              </div>
            )}

            {/* Sentiment — composition bar, semantic colors */}
            {sentimentBuckets.length > 0 && sentimentTotal > 0 && (
              <div className="st-card">
                <div className="st-card-header">
                  <h2>{t('statisticsPage.sentimentTitle', 'Sentiment client')}</h2>
                  {typeof sentiment.overall_average === 'number' && (
                    <span className="st-card-meta">
                      <span className={`st-tone-dot ${sentimentTone(sentiment.overall_average)}`} aria-hidden="true" />
                      {t('statisticsPage.sentimentAvg', 'score moyen {{score}}/100', { score: Math.round(sentiment.overall_average) })}
                    </span>
                  )}
                </div>
                <div className="st-sentiment">
                  <div className="st-comp-bar" role="img" aria-label={t('statisticsPage.sentimentTitle', 'Sentiment client')}>
                    {sentimentBuckets.map((b) => (
                      <span key={b.key} className={`st-comp-seg ${b.cls}`} style={{ width: `${(b.count / sentimentTotal) * 100}%` }} />
                    ))}
                  </div>
                  <div className="st-comp-legend">
                    {sentimentBuckets.map((b) => (
                      <span key={b.key} className="st-comp-item">
                        <span className="st-comp-swatch" style={{ background: b.color }} aria-hidden="true" />
                        {b.label} · {fmtNum(b.count)} ({b.percentage >= 10 ? Math.round(b.percentage) : b.percentage.toFixed(1)} %)
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Statuses — humanized labels + semantic dots, neutral bars */}
            {(overviewData?.status?.statuses || []).length > 0 && (
              <BreakdownCard
                title={t('statisticsPage.statusTitle', 'Statuts')}
                meta={t('statisticsPage.scopeTickets', '{{total}} tickets', { total: fmtNum(overviewData.status.total_tickets) })}
                totalForPct={overviewData.status.total_tickets}
                rows={rollup(
                  overviewData.status.statuses, 7,
                  (s) => getStatusLabel(t, s.status),
                  (s) => s.count
                ).map((r) => ({
                  ...r,
                  key: r.item?.status || r.label,
                  dot: r.item ? (STATUS_DOT[getStatusClass(r.item.status)] || 'var(--blue)') : undefined,
                }))}
              />
            )}

            {/* Motifs — horizontal bar chart with classification coverage */}
            {motifsData && (
              <div className="st-card">
                <div className="st-card-header">
                  <h2>{t('statisticsPage.motifsTitle', 'Motifs')}</h2>
                  <span className="st-card-meta">
                    {t('statisticsPage.motifsCoverage', '{{pct}} % classés · {{count}} non classés', {
                      pct: typeof motifsData.coverage_percentage === 'number' ? motifsData.coverage_percentage : 0,
                      count: fmtNum(motifsData.unclassified_tickets || 0),
                    })}
                  </span>
                </div>
                {motifsData.classified_tickets === 0 || !motifChartData ? (
                  <p className="st-card-empty">{t('statisticsPage.noClassified', 'Aucun ticket classé sur la période.')}</p>
                ) : (
                  <div className="st-bar-body" style={{ height: Math.max(140, motifRows.length * 27 + 42) }}>
                    <Bar data={motifChartData} options={motifChartOptions} />
                  </div>
                )}
              </div>
            )}

            {/* Carriers */}
            {(overviewData?.carriers?.carriers || []).length > 0 && (
              <BreakdownCard
                title={t('statisticsPage.carriersTitle', 'Transporteurs')}
                meta={t('statisticsPage.carriersMeta', '{{total}} tickets avec transporteur', { total: fmtNum(overviewData.carriers.total_with_carrier) })}
                totalForPct={overviewData.carriers.total_with_carrier}
                rows={rollup(
                  overviewData.carriers.carriers, 8,
                  (c) => c.carrier,
                  (c) => c.count
                ).map((r) => ({
                  ...r,
                  key: r.item?.carrier || r.label,
                  subLabel: serviceSummary(r.item?.services),
                  title: r.item ? [r.label, serviceSummary(r.item.services, 99)].filter(Boolean).join(' — ') : r.label,
                }))}
              />
            )}

            {/* Channels */}
            {(overviewData?.channels?.channels || []).length > 0 && (
              <BreakdownCard
                title={t('statisticsPage.channelsTitle', 'Canaux')}
                meta={t('statisticsPage.scopeTickets', '{{total}} tickets', { total: fmtNum(overviewData.channels.total_tickets) })}
                totalForPct={overviewData.channels.total_tickets}
                rows={rollup(
                  overviewData.channels.channels, 8,
                  (c) => c.channel_name,
                  (c) => c.ticket_count
                ).map((r) => ({ ...r, key: r.item?.channel_name || r.label }))}
              />
            )}

            {/* Brands — only meaningful without a brand filter and with >1 brand */}
            {!selectedBrand && brandRowsRaw.length > 1 && (
              <BreakdownCard
                title={t('statisticsPage.brandsTitle', 'Marques')}
                meta={t('statisticsPage.scopeTickets', '{{total}} tickets', { total: fmtNum(overviewData.brands.total_tickets) })}
                totalForPct={overviewData.brands.total_tickets}
                rows={rollup(
                  brandRowsRaw, 8,
                  (b) => b.label,
                  (b) => b.count
                ).map((r) => ({ ...r, key: r.label }))}
              />
            )}

            {/* Top clients — only without a client filter */}
            {!selectedClient && topClientRows.length > 0 && (
              <BreakdownCard
                title={t('statisticsPage.topClientsTitle', 'Top clients')}
                meta={t('statisticsPage.topClientsMeta', '{{total}} tickets avec client', { total: fmtNum(topClients.total_with_client) })}
                tone="teal"
                totalForPct={topClients.total_with_client}
                rows={topClientRows.map((c) => ({ key: c.client, label: c.client, count: c.total }))}
              />
            )}

            {/* AI tools — violet, calls + tickets, with tools-per-ticket histogram */}
            {(aiTools?.tools || []).length > 0 && (
              <BreakdownCard
                title={t('statisticsPage.aiToolsTitle', 'Outils IA')}
                meta={t('statisticsPage.aiToolsMeta', '{{total}} appels', { total: fmtNum(aiTools.total_tool_calls) })}
                tone="violet"
                totalForPct={aiTools.total_tool_calls}
                rows={aiTools.tools.map((tool) => ({
                  key: tool.tool_name,
                  label: toolLabel(tool.tool_name),
                  title: tool.tool_name,
                  count: tool.call_count,
                  subLabel: typeof tool.tickets_used_in === 'number'
                    ? t('statisticsPage.usedInTickets', 'dans {{total}} tickets', { total: tool.tickets_used_in.toLocaleString(lang) })
                    : null,
                }))}
              >
                {toolsHistogram.length > 1 && (
                  <div className="st-histogram">
                    <div className="st-histogram-header">
                      <h3>{t('statisticsPage.toolsPerTicketTitle', 'Outils utilisés par ticket')}</h3>
                      {typeof toolsPerTicket?.average_tools_per_ticket === 'number' && (
                        <span>{t('statisticsPage.toolsPerTicketAvg', 'moyenne {{avg}}', { avg: toolsPerTicket.average_tools_per_ticket })}</span>
                      )}
                    </div>
                    <div className="st-histogram-bars">
                      {toolsHistogram.map((b) => (
                        <div
                          key={b.label}
                          className="st-histogram-col"
                          title={t('statisticsPage.toolsPerTicketTooltip', '{{tickets}} tickets · {{tools}} outils', { tickets: fmtNum(b.count), tools: b.label })}
                        >
                          <span className="st-histogram-bar">
                            <span className="st-histogram-fill" style={{ height: `${(b.count / maxHistogram) * 100}%` }} />
                          </span>
                          <span className="st-histogram-x">{b.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </BreakdownCard>
            )}

            {/* Carrier performance — plain table, full width */}
            {carrierPerf.length > 0 && (
              <div className="st-card st-span-full">
                <div className="st-card-header">
                  <h2>{t('statisticsPage.carrierPerfTitle', 'Performance transporteurs')}</h2>
                  <span className="st-card-meta">
                    {t('statisticsPage.carrierPerfMeta', '{{total}} transporteurs', { total: fmtNum(carrierPerf.length) })}
                  </span>
                </div>
                <div className="st-table-wrap">
                  <table className="st-table">
                    <thead>
                      <tr>
                        <th>{t('statisticsPage.colCarrier', 'Transporteur')}</th>
                        <th className="num">{t('statisticsPage.colTickets', 'Tickets')}</th>
                        <th className="num">{t('statisticsPage.colResolvedRate', '% résolus')}</th>
                        <th className="num">{t('statisticsPage.colAiRate', '% IA')}</th>
                        <th className="num">{t('statisticsPage.colAvgResolution', 'Résolution moy.')}</th>
                        <th className="num">{t('statisticsPage.colSentiment', 'Sentiment')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carrierPerf.map((c) => (
                        <tr key={c.carrier}>
                          <td className="st-td-label" title={[c.carrier, serviceSummary(c.services, 99)].filter(Boolean).join(' — ')}>{c.carrier}</td>
                          <td className="num">
                            <span className="st-tickets-cell">
                              <span>{fmtNum(c.total)}</span>
                              <span className="st-cell-bar" aria-hidden="true">
                                <span style={{ width: `${((c.total || 0) / maxCarrierTotal) * 100}%` }} />
                              </span>
                            </span>
                          </td>
                          <td className="num">{fmtPct(c.resolution_rate)}</td>
                          <td className="num">{fmtPct(c.ai_resolution_rate)}</td>
                          <td className="num">{typeof c.avg_resolution_hours === 'number' ? fmtHours(c.avg_resolution_hours) : '—'}</td>
                          <td className="num">
                            {typeof c.avg_sentiment === 'number' ? (
                              <span className="st-sentiment-cell">
                                <span className={`st-tone-dot ${sentimentTone(c.avg_sentiment)}`} aria-hidden="true" />
                                {Math.round(c.avg_sentiment)}
                                <small>/100</small>
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Statistics;
